<script setup lang="ts">
import { HgButton } from '@smg-real-estate/domus';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import gradientHeartUrl from '@/assets/images/effects/gradient-heart.webp';
import chartDesktopDeHg from '@/assets/images/property-prices/chart-desktop-de-hg.svg?url';
import chartDesktopDeIs24 from '@/assets/images/property-prices/chart-desktop-de-is24.svg?url';
import chartDesktopEnHg from '@/assets/images/property-prices/chart-desktop-en-hg.svg?url';
import chartDesktopEnIs24 from '@/assets/images/property-prices/chart-desktop-en-is24.svg?url';
import chartDesktopFrHg from '@/assets/images/property-prices/chart-desktop-fr-hg.svg?url';
import chartDesktopFrIs24 from '@/assets/images/property-prices/chart-desktop-fr-is24.svg?url';
import chartDesktopItHg from '@/assets/images/property-prices/chart-desktop-it-hg.svg?url';
import chartDesktopItIs24 from '@/assets/images/property-prices/chart-desktop-it-is24.svg?url';
import chartMobileDeHg from '@/assets/images/property-prices/chart-mobile-de-hg.svg?url';
import chartMobileDeIs24 from '@/assets/images/property-prices/chart-mobile-de-is24.svg?url';
import chartMobileEnHg from '@/assets/images/property-prices/chart-mobile-en-hg.svg?url';
import chartMobileEnIs24 from '@/assets/images/property-prices/chart-mobile-en-is24.svg?url';
import chartMobileFrHg from '@/assets/images/property-prices/chart-mobile-fr-hg.svg?url';
import chartMobileFrIs24 from '@/assets/images/property-prices/chart-mobile-fr-is24.svg?url';
import chartMobileItHg from '@/assets/images/property-prices/chart-mobile-it-hg.svg?url';
import chartMobileItIs24 from '@/assets/images/property-prices/chart-mobile-it-is24.svg?url';
import mapDeHg from '@/assets/images/property-prices/map-de-hg.jpg';
import mapDeIs24 from '@/assets/images/property-prices/map-de-is24.jpg';
import mapEnHg from '@/assets/images/property-prices/map-en-hg.jpg';
import mapEnIs24 from '@/assets/images/property-prices/map-en-is24.jpg';
import mapFrHg from '@/assets/images/property-prices/map-fr-hg.jpg';
import mapFrIs24 from '@/assets/images/property-prices/map-fr-is24.jpg';
import mapItHg from '@/assets/images/property-prices/map-it-hg.jpg';
import mapItIs24 from '@/assets/images/property-prices/map-it-is24.jpg';
import SectionWithAnchor from '@/components/common/SectionWithAnchor/SectionWithAnchor.vue';
import { getCurrentPlatform, isPlatformIs24 } from '@/helpers/Platform/Platform';
import { useCommonStore } from '@/stores/common/common';
import { trackingGenericClickEvent, trackingLandingPageButtonEvent } from '@/tracking/tracking';
import {
  LANDING_PAGE_BUTTON_ACTION,
  LOCAL_LANDING_PAGE_ACTION,
  LOCAL_LANDING_PAGE_CATEGORY,
  LOCAL_LANDING_PAGE_EVENT,
} from '@/tracking/types';
import { AnchorLinks, Pages } from '@/types';

const { locale } = useI18n();
const commonStore = useCommonStore();

const platform = getCurrentPlatform();
const isIs24 = isPlatformIs24();

const mapImages: Record<string, string> = {
  de_hg: mapDeHg,
  de_is24: mapDeIs24,
  en_hg: mapEnHg,
  en_is24: mapEnIs24,
  fr_hg: mapFrHg,
  fr_is24: mapFrIs24,
  it_hg: mapItHg,
  it_is24: mapItIs24,
};

const chartDesktopImages: Record<string, string> = {
  de_hg: chartDesktopDeHg,
  de_is24: chartDesktopDeIs24,
  en_hg: chartDesktopEnHg,
  en_is24: chartDesktopEnIs24,
  fr_hg: chartDesktopFrHg,
  fr_is24: chartDesktopFrIs24,
  it_hg: chartDesktopItHg,
  it_is24: chartDesktopItIs24,
};

const chartMobileImages: Record<string, string> = {
  de_hg: chartMobileDeHg,
  de_is24: chartMobileDeIs24,
  en_hg: chartMobileEnHg,
  en_is24: chartMobileEnIs24,
  fr_hg: chartMobileFrHg,
  fr_is24: chartMobileFrIs24,
  it_hg: chartMobileItHg,
  it_is24: chartMobileItIs24,
};

const currentPage = computed(() => commonStore.currentPage);
const imageKey = computed(() => `${locale.value}_${platform}`);
const mapImage = computed(() => mapImages[imageKey.value] ?? mapImages[`de_${platform}`]);
const chartImageDesktop = computed(
  () => chartDesktopImages[imageKey.value] ?? chartDesktopImages[`de_${platform}`],
);
const chartImageMobile = computed(
  () => chartMobileImages[imageKey.value] ?? chartMobileImages[`de_${platform}`],
);

const getMarketAtlasRoute = computed(() => {
  const localizedRoutes = {
    de: '/de/immobilienpreise-pro-m2',
    fr: '/fr/prix-immobilier-m2',
    it: '/it/prezzi-immobili-m2',
    en: '/en/property-prices-m2',
  };
  return localizedRoutes[locale.value];
});

const isFromLLP = computed(() => {
  return currentPage.value === Pages.LLP;
});

// Methods
const onClick = () => {
  console.log('click Market Atlas', currentPage.value);
  if (currentPage.value === Pages.LLP) {
    trackingGenericClickEvent({
      event: LOCAL_LANDING_PAGE_EVENT.CTA_CLICK,
      category: LOCAL_LANDING_PAGE_CATEGORY.CTA_CLICK,
      action: LOCAL_LANDING_PAGE_ACTION.MARKET_ATLAS,
    });
  } else if (currentPage.value === Pages.LP) {
    trackingLandingPageButtonEvent(LANDING_PAGE_BUTTON_ACTION.OPEN_MARKET_ATLAS);
  }
};

const buttonTheme = 'secondary' as const;
</script>

<template>
  <SectionWithAnchor :anchorId="isFromLLP ? AnchorLinks.MarketAtlas : undefined">
    <div :class="['market-atlas-section', isIs24 ? 'is24' : 'hg', { ['llp']: isFromLLP }]">
      <div class="row">
        <div class="col-md-10 offset-md-1">
          <div class="row content-wrapper">
            <div
              class="col-sm-6"
              :class="[isFromLLP ? 'content-llp' : 'content', { ['is24']: isIs24 }]"
            >
              <h3>{{ $t('landingPage.marketAtlas.title') }}</h3>
              <p>
                {{ $t('landingPage.marketAtlas.content') }}
              </p>
              <HgButton
                tag="a"
                :href="getMarketAtlasRoute"
                :theme="buttonTheme"
                target="_blank"
                rel="noopener"
                @click="onClick"
              >
                {{ $t('landingPage.marketAtlas.buttonText') }}
              </HgButton>
            </div>
            <div class="col-sm-6 images">
              <div class="image-container">
                <img
                  v-if="!isIs24"
                  class="gradient-heart"
                  :src="gradientHeartUrl"
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                />
                <img
                  :src="mapImage"
                  loading="lazy"
                  :class="[isFromLLP ? 'img llp' : 'img lp']"
                  :alt="$t('landingPage.marketAtlas.mapImgAlt')"
                />
                <img
                  :src="chartImageDesktop"
                  loading="lazy"
                  class="img-chart desktop"
                  :class="[{ ['local-landing-page-desktop']: isFromLLP }, { ['is24']: isIs24 }]"
                  :alt="$t('landingPage.marketAtlas.priceTrendImgAlt')"
                />
                <img
                  :src="chartImageMobile"
                  loading="lazy"
                  class="img-chart mobile"
                  :class="[{ ['local-landing-page-mobile']: isFromLLP }, { ['is24']: isIs24 }]"
                  :alt="$t('landingPage.marketAtlas.priceTrendImgAlt')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </SectionWithAnchor>
</template>

<style lang="scss" scoped>
@use '@/assets/variables' as *;

h3 {
  margin-top: $space-700;

  @include grid-media-query(sm) {
    margin-top: $space-150;
  }

  @include heading-3;
}

.market-atlas-section {
  &.llp {
    @include grid-media-query(sm) {
      &.hg {
        margin-bottom: $space-700;
      }

      .is24 {
        margin-bottom: $space-600;
      }
    }

    .gradient-heart {
      @include grid-media-query(sm) {
        left: 25%;
        right: auto;
      }
    }
  }

  .content {
    order: 1;

    @include grid-media-query(md) {
      padding-right: $space-150;
    }

    p {
      @include body-1;
    }
  }

  .content-llp {
    order: 0;

    &.is24 {
      align-content: center;
      order: 1;
    }

    @include grid-media-query(md) {
      padding-right: $space-150;
    }

    p {
      @include body-1;
    }
  }

  .images {
    @include grid-media-query(sm) {
      order: 2;
    }
  }
}

.image-container {
  position: relative;
  text-align: right;

  .img {
    width: 100%;
    object-fit: cover;
    border-radius: $border-radius-l;
    z-index: 1;

    &.lp {
      height: 360px;
    }

    &.llp {
      height: 252px;
    }
  }

  .gradient-heart {
    position: absolute;
    bottom: -$space-1000;
    left: 35%;
    z-index: -1;
    width: calc($space-1000 * 2 + $space-500);
    height: calc($space-1000 * 2 + $space-500);
    pointer-events: none;
    object-fit: cover;
    transform: translateX(-33%) rotate(90deg);

    @include grid-media-query(sm) {
      left: 25%;
      object-fit: contain;
    }

    @include grid-media-query(md) {
      left: auto;
      right: -$space-1000 * 2;
      top: -$space-350;
      width: calc($space-1000 * 3);
      height: calc($space-1000 * 3);
    }
  }

  .img-chart {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 272px;
    border-radius: $border-radius-l;
    box-shadow: 0 0 64px 0 #0003;
    z-index: 2;

    &.desktop {
      display: none;

      @include grid-media-query(md) {
        display: block;
        transform: translate(-20px, -25%);
      }
    }

    &.mobile {
      display: block;
      transform: translate(30px, 35%);

      @include grid-media-query(sm) {
        transform: translate(30px, 25%);
      }

      @include grid-media-query(md) {
        display: none;
      }
    }

    &.local-landing-page-desktop {
      display: none;

      &.is24 {
        display: none;
        transform: translate(50%, 60%);
        z-index: 0;

        @include grid-media-query(md) {
          display: block;
        }
      }
    }

    &.local-landing-page-mobile {
      display: block;
      transform: translate(50%, 60%);

      &.is24 {
        z-index: 0;

        @include grid-media-query(md) {
          display: none;
        }
      }
    }
  }
}

.market-atlas-section.hg {
  margin-bottom: $space-1000;

  @include grid-media-query(md) {
    margin-bottom: $space-500;
  }

  h3 {
    margin-top: $space-200;

    @include grid-media-query(sm) {
      margin-top: $space-100;
    }
  }

  .content-wrapper {
    flex-flow: column nowrap;
    gap: $space-250;

    @include grid-media-query(sm) {
      flex-direction: row-reverse;
    }
  }

  .content {
    flex: 1;
    order: 0;

    @include grid-media-query(md) {
      padding-left: $space-700;
    }

    @include grid-media-query(lg) {
      padding-top: $space-150;
      padding-bottom: $space-150;
    }
  }

  .images {
    flex: 1;
  }

  .img-chart {
    &.mobile {
      left: 50%;
      transform: translate(-50%, 42%);
    }

    &.desktop {
      @include grid-media-query(md) {
        top: 50%;
        left: 50%;
        transform: translate(10%, -50%);
      }

      @include grid-media-query(lg) {
        transform: translate(10%, -50%);
      }
    }
  }
}
</style>
